<template>
    <DsModal
        data-qa="rename-modal"
        is-open
        :show-close="!createCategoryModeEnabled"
        :show-back="createCategoryModeEnabled"
        size="sm"
        dialog
        :title="getTitle"
        @close="closeEditNameAndCategoryModal"
        @back="toggleCreateCategoryMode(false)"
    >
        <template #header>
            <DsFilledButton
                data-qa="save-button"
                @click="handleSave"
            >
                {{ $t('funnel.edit.save') }}
            </DsFilledButton>
        </template>
        <template #default>
            <DsInputField
                v-show="!createCategoryModeEnabled"
                v-model="newFunnelName"
                :label="$t('funnel.edit.title')"
                data-qa="funnel-name"
                tabindex="1"
                @keyup.enter="handleSave"
                @keyup.esc="closeEditNameAndCategoryModal"
            />

            <DsMultiselect
                v-show="!createCategoryModeEnabled"
                v-model="categoriesSelected"
                allow-add
                :add-text="$t('funnel.categories.create.new.label')"
                multiple
                searchable
                label-prop="label"
                value-prop="value"
                :placeholder="$t('funnel.categories.campaign.category')"
                bind-value-only
                data-qa="categories-selected"
                tabindex="2"
                :options="allCategories"
                @add="toggleCreateCategoryMode(true)"
                @updateSearch="handleInputChange"
            />

            <DsInputField
                v-show="createCategoryModeEnabled"
                v-model="newCategoryName"
                :placeholder="$t('funnel.categories.create.campaign.name')"
                :label="$t('funnel.categories.create.campaign.name')"
                data-qa="add-new-category"
            />

            <DsInlineAlert
                v-if="showDuplicateCategoryError"
                leading-icon
                class="alert-duplicate-name"
                type="critical"
                data-qa="add-new-category-alert"
            >
                <span>{{ $t('funnel.categories.create.exists.error') }}</span>
            </DsInlineAlert>
        </template>
    </DsModal>
</template>

<script>
import { rename } from './rename.api';
import { saveCategories, createNewCategories } from './edit.name.and.category.api';
import { utils } from '../../utils/legacy-utils';

export default {
    props: {
        funnelId: {
            type: Number,
            default: 0,
        },
        funnelName: {
            type: String,
            required: true,
        },
        allCategories: {
            type: Array,
            required: true,
        },
        assignedCategories: {
            type: Array,
            required: true,
        },
    },

    emits: ['update-name', 'update-categories', 'close-name-and-category', 'refresh-automation-categories'],
    data() {
        return {
            categoriesSelected: [],
            newFunnelName: this.funnelName,
            createCategoryModeEnabled: false,
            newCategoryName: '',
            categorySearchInput: '',
            isDuplicateCategoryName: false,
            newlyAddedCategories: [],
        };
    },

    computed: {
        allCategoriesAssigned() {
            return this.assignedCategories.map((category) => category.value);
        },

        categoriesToAdd() {
            return utils.getSetDifference(this.categoriesSelected, this.allCategoriesAssigned);
        },

        categoriesToRemove() {
            return utils.getSetDifference(this.allCategoriesAssigned, this.categoriesSelected);
        },

        getTitle() {
            if (this.createCategoryModeEnabled) {
                return this.$t('funnel.categories.create.campaign.category');
            }

            return this.$t('funnel.categories.rename.modal.title');
        },

        showDuplicateCategoryError() {
            return this.createCategoryModeEnabled && this.isDuplicateCategoryName;
        },
    },

    watch: {
        assignedCategories: {
            handler() {
                this.updateCategoriesSelected();
            },
            immediate: true,
            deep: true,
        },
    },

    mounted() {
        this.updateCategoriesSelected();
    },

    methods: {
        handleInputChange(value) {
            this.categorySearchInput = value;
        },

        updateCategoriesSelected() {
            this.categoriesSelected = this.assignedCategories.map((category) => category.value);

            this.newlyAddedCategories.forEach((category) => this.categoriesSelected.push(category));
        },

        async handleSave() {
            if (this.createCategoryModeEnabled) {
                await this.createNewCategory();
            } else {
                await this.saveNameAndCategories();
            }
        },

        async createNewCategory() {
            try {
                this.newlyAddedCategories = [];

                const categoryNames = this.allCategories.map((category) => category.label);

                if (categoryNames.includes(this.newCategoryName)) {
                    this.isDuplicateCategoryName = true;
                } else {
                    this.isDuplicateCategoryName = false;

                    this.newlyAddedCategories = await createNewCategories([this.newCategoryName]);

                    this.refreshAutomationCategories();
                    this.toggleCreateCategoryMode(false);
                    this.newCategoryName = '';
                }
            } catch (error) {
                this.$error({ message: error });
            }
        },

        async saveNameAndCategories() {
            try {
                const titleUpdated = this.newFunnelName !== this.funnelName;
                const categoriesUpdated = this.assignedCategories.length !== this.categoriesSelected.length
                        || this.assignedCategories.length !== this.assignedCategories.filter((item) => this.categoriesSelected.includes(item.value)).length;

                if (titleUpdated) {
                    await rename(this.funnelId, this.newFunnelName || this.$t('funnel.name.unknown'));

                    this.$emit('update-name', this.newFunnelName || this.$t('funnel.name.unknown'));
                }

                if (categoriesUpdated) {
                    await saveCategories(
                        this.funnelId,
                        this.categoriesToAdd,
                        this.categoriesToRemove,
                    );

                    this.$emit('update-categories', this.categoriesSelected);
                }

                if (titleUpdated && categoriesUpdated) {
                    this.$toast({ message: this.$t('funnel.categories.withName.update.success') });
                } else if (!titleUpdated && categoriesUpdated) {
                    this.$toast({ message: this.$t('funnel.categories.update.success') });
                } else if (titleUpdated && !categoriesUpdated) {
                    this.$toast({ message: this.$t('funnel.title.update.success') });
                }

                this.closeEditNameAndCategoryModal();
            } catch (error) {
                this.$error({ message: error });
            }
        },

        toggleCreateCategoryMode(isEnabled) {
            this.createCategoryModeEnabled = isEnabled;
            this.newCategoryName = this.categorySearchInput;

            if (!isEnabled) {
                this.newCategoryName = '';
                this.isDuplicateCategoryName = false;
            }
        },

        closeEditNameAndCategoryModal() {
            this.$emit('close-name-and-category');
        },

        refreshAutomationCategories() {
            this.$emit('refresh-automation-categories');
        },
    },
};

</script>
